// Martinez Law — Bilingual Content Data Layer

export type Locale = "en" | "es";

export interface LocalizedString {
  en: string;
  es: string;
}

export interface NavItem {
  label: LocalizedString;
  href: string;
  esHref?: string;
  children?: NavItem[];
}

export interface PracticeArea {
  id: string;
  icon: string;
  title: LocalizedString;
  summary: LocalizedString;
  href: string;
  esHref: string;
}

export interface Differentiator {
  icon: string;
  title: LocalizedString;
  description: LocalizedString;
}

export interface CaseResult {
  tag: LocalizedString;
  headline: LocalizedString;
  summary: LocalizedString;
}

export interface Office {
  city: LocalizedString;
  address: string;
  phone: string;
  mapUrl: string;
}

export interface ResourceTeaser {
  icon: string;
  title: LocalizedString;
  description: LocalizedString;
  href: string;
  esHref: string;
}

// ─── Navigation ───

export const navigation: NavItem[] = [
  { label: { en: "Home", es: "Inicio" }, href: "/", esHref: "/es" },
  {
    label: { en: "Practice Areas", es: "Áreas de Práctica" },
    href: "/practice-areas",
    esHref: "/es/areas-de-practica",
    children: [
      { label: { en: "Personal Injury", es: "Lesiones Personales" }, href: "/practice-areas/personal-injury", esHref: "/es/areas-de-practica/lesiones-personales" },
      { label: { en: "Business Law & Licensing", es: "Derecho Comercial y Licencias" }, href: "/practice-areas/business-law", esHref: "/es/areas-de-practica/derecho-comercial" },
      { label: { en: "Estate Planning", es: "Planificación Patrimonial" }, href: "/practice-areas/estate-planning", esHref: "/es/areas-de-practica/planificacion-patrimonial" },
      { label: { en: "Divorce & Family Law", es: "Divorcio y Derecho Familiar" }, href: "/practice-areas/family-law", esHref: "/es/areas-de-practica/derecho-familiar" },
      { label: { en: "Criminal Defense", es: "Defensa Criminal" }, href: "/practice-areas/criminal-defense", esHref: "/es/areas-de-practica/defensa-criminal" },
      { label: { en: "Construction Law", es: "Derecho de Construcción" }, href: "/practice-areas/construction-law", esHref: "/es/areas-de-practica/derecho-construccion" },
      { label: { en: "Real Estate & Zoning", es: "Bienes Raíces y Zonificación" }, href: "/practice-areas/real-estate", esHref: "/es/areas-de-practica/bienes-raices" },
      { label: { en: "Wills, Trusts & Probate", es: "Testamentos y Sucesiones" }, href: "/practice-areas/wills-trusts-probate", esHref: "/es/areas-de-practica/testamentos-sucesiones" },
      { label: { en: "Immigration", es: "Inmigración" }, href: "/practice-areas/immigration", esHref: "/es/areas-de-practica/inmigracion" },
      { label: { en: "Traffic Defense", es: "Defensa de Tráfico" }, href: "/practice-areas/traffic-defense", esHref: "/es/areas-de-practica/defensa-trafico" },
    ],
  },
  { label: { en: "Resources", es: "Recursos" }, href: "/resources", esHref: "/es/recursos" },
  { label: { en: "Locations", es: "Ubicaciones" }, href: "/locations", esHref: "/es/ubicaciones" },
  { label: { en: "About", es: "Nosotros" }, href: "/about", esHref: "/es/nosotros" },
  { label: { en: "Contact", es: "Contacto" }, href: "/contact", esHref: "/es/contacto" },
];

// ─── Hero ───

export const hero = {
  headline: {
    en: "When everything is on the line, precision matters.",
    es: "Cuando todo está en juego, la precisión importa.",
  },
  subline: {
    en: "Litigation-led counsel for urgent disputes, serious legal problems, and clients who need action without delay.",
    es: "Asesoría liderada por litigio para disputas urgentes, problemas legales serios, y clientes que necesitan acción sin demora.",
  },
  cta: {
    book: { en: "Book Consultation", es: "Agendar Consulta" },
    call: { en: "Call Now", es: "Llamar Ahora" },
    whatsapp: { en: "WhatsApp", es: "WhatsApp" },
  },
  trustMarkers: [
    { en: "4.9★ Google Reviews", es: "4.9★ Reseñas de Google" },
    { en: "1,500+ Clients Helped", es: "1,500+ Clientes Atendidos" },
    { en: "$2M+ Recovered", es: "$2M+ Recuperados" },
    { en: "3 Offices · Bilingual", es: "3 Oficinas · Bilingüe" },
  ],
  portalLink: {
    en: "Existing client? Access portal",
    es: "¿Cliente existente? Acceder al portal",
  },
};

// ─── Section Titles ───

export const sectionTitles: Record<string, LocalizedString> = {
  practiceAreas: { en: "Practice Areas", es: "Áreas de Práctica" },
  practiceAreasSubtitle: { en: "Strategic counsel across every front.", es: "Asesoría estratégica en todos los frentes." },
  whyMartinez: { en: "Why Martinez Law", es: "Por Qué Martinez Law" },
  whyMartinezSubtitle: { en: "What sets us apart.", es: "Lo que nos diferencia." },
  results: { en: "Representative Matters", es: "Asuntos Representativos" },
  resultsSubtitle: { en: "Outcomes that speak for themselves.", es: "Resultados que hablan por sí mismos." },
  attorney: { en: "Meet the Firm", es: "Conozca la Firma" },
  howItWorks: { en: "The Process", es: "El Proceso" },
  howItWorksSubtitle: { en: "From first call to resolution.", es: "Desde la primera llamada hasta la resolución." },
  locations: { en: "Our Offices", es: "Nuestras Oficinas" },
  locationsSubtitle: { en: "Three locations across Chicagoland.", es: "Tres ubicaciones en la región de Chicago." },
  resources: { en: "Resources", es: "Recursos" },
  resourcesSubtitle: { en: "Tools, guides, and insights.", es: "Herramientas, guías y análisis." },
};

// ─── Practice Areas ───

export const practiceAreas: PracticeArea[] = [
  { id: "personal-injury", icon: "Shield", title: { en: "Personal Injury", es: "Lesiones Personales" }, summary: { en: "Aggressive recovery for accident victims. We fight for the compensation you deserve.", es: "Recuperación agresiva para víctimas de accidentes. Luchamos por la compensación que merece." }, href: "/practice-areas/personal-injury", esHref: "/es/areas-de-practica/lesiones-personales" },
  { id: "business-law", icon: "Briefcase", title: { en: "Business Law & Licensing", es: "Derecho Comercial y Licencias" }, summary: { en: "Formation, contracts, licensing, and disputes — protecting your business at every stage.", es: "Formación, contratos, licencias y disputas — protegiendo su negocio en cada etapa." }, href: "/practice-areas/business-law", esHref: "/es/areas-de-practica/derecho-comercial" },
  { id: "estate-planning", icon: "FileText", title: { en: "Estate Planning", es: "Planificación Patrimonial" }, summary: { en: "Protect your legacy with wills, trusts, and comprehensive estate strategies.", es: "Proteja su legado con testamentos, fideicomisos y estrategias patrimoniales integrales." }, href: "/practice-areas/estate-planning", esHref: "/es/areas-de-practica/planificacion-patrimonial" },
  { id: "family-law", icon: "Heart", title: { en: "Divorce & Family Law", es: "Divorcio y Derecho Familiar" }, summary: { en: "Custody, divorce, support — resolving family matters with strength and sensitivity.", es: "Custodia, divorcio, manutención — resolviendo asuntos familiares con firmeza y sensibilidad." }, href: "/practice-areas/family-law", esHref: "/es/areas-de-practica/derecho-familiar" },
  { id: "criminal-defense", icon: "Scale", title: { en: "Criminal Defense", es: "Defensa Criminal" }, summary: { en: "Misdemeanors to felonies — experienced courtroom defense when your freedom is at stake.", es: "Desde infracciones hasta delitos graves — defensa experimentada cuando su libertad está en juego." }, href: "/practice-areas/criminal-defense", esHref: "/es/areas-de-practica/defensa-criminal" },
  { id: "construction-law", icon: "HardHat", title: { en: "Construction Law", es: "Derecho de Construcción" }, summary: { en: "Liens, disputes, contract enforcement — protecting builders and property owners.", es: "Gravámenes, disputas, cumplimiento de contratos — protegiendo constructores y propietarios." }, href: "/practice-areas/construction-law", esHref: "/es/areas-de-practica/derecho-construccion" },
  { id: "real-estate", icon: "Home", title: { en: "Real Estate & Zoning", es: "Bienes Raíces y Zonificación" }, summary: { en: "Closings, zoning, title issues — trusted guidance for real estate transactions.", es: "Cierres, zonificación, problemas de título — orientación confiable para transacciones inmobiliarias." }, href: "/practice-areas/real-estate", esHref: "/es/areas-de-practica/bienes-raices" },
  { id: "wills-trusts", icon: "ScrollText", title: { en: "Wills, Trusts & Probate", es: "Testamentos y Sucesiones" }, summary: { en: "Probate administration, will contests, and trust management with care and precision.", es: "Administración sucesoria, impugnación de testamentos y gestión de fideicomisos con cuidado y precisión." }, href: "/practice-areas/wills-trusts-probate", esHref: "/es/areas-de-practica/testamentos-sucesiones" },
  { id: "immigration", icon: "Globe", title: { en: "Immigration", es: "Inmigración" }, summary: { en: "Visas, green cards, deportation defense, and family reunification — in your language.", es: "Visas, residencia, defensa contra deportación y reunificación familiar — en su idioma." }, href: "/practice-areas/immigration", esHref: "/es/areas-de-practica/inmigracion" },
  { id: "traffic-defense", icon: "Car", title: { en: "Traffic Defense", es: "Defensa de Tráfico" }, summary: { en: "Speeding, DUI, license issues — keeping you on the road and out of trouble.", es: "Exceso de velocidad, DUI, problemas de licencia — manteniéndolo en el camino y sin problemas." }, href: "/practice-areas/traffic-defense", esHref: "/es/areas-de-practica/defensa-trafico" },
];

// ─── Why Martinez Law ───

export const differentiators: Differentiator[] = [
  { icon: "Zap", title: { en: "Fast Response", es: "Respuesta Rápida" }, description: { en: "We answer calls and respond to inquiries within hours — not days. When your case matters, speed matters.", es: "Respondemos llamadas y consultas en horas — no en días. Cuando su caso importa, la velocidad importa." } },
  { icon: "Languages", title: { en: "Bilingual Counsel", es: "Asesoría Bilingüe" }, description: { en: "Full legal representation in English and Spanish. No interpreters, no confusion — clear counsel in your language.", es: "Representación legal completa en inglés y español. Sin intérpretes, sin confusión — asesoría clara en su idioma." } },
  { icon: "Gavel", title: { en: "Courtroom Strength", es: "Fortaleza en el Tribunal" }, description: { en: "We prepare every case as if it's going to trial. Insurance companies and opposing counsel know we don't back down.", es: "Preparamos cada caso como si fuera a juicio. Las aseguradoras y abogados contrarios saben que no retrocedemos." } },
  { icon: "MapPin", title: { en: "Regional Reach", es: "Alcance Regional" }, description: { en: "Three offices across the Chicago region — Chicago, Aurora, and Kankakee — with Federal Court admission covering Illinois, Indiana, and Wisconsin.", es: "Tres oficinas en la región de Chicago — Chicago, Aurora y Kankakee — con admisión en Tribunales Federales de Illinois, Indiana y Wisconsin." } },
  { icon: "Eye", title: { en: "Transparent Process", es: "Proceso Transparente" }, description: { en: "You'll always know where your case stands, what comes next, and what it will cost. No surprises.", es: "Siempre sabrá el estado de su caso, qué sigue y cuánto costará. Sin sorpresas." } },
];

// ─── Featured Results ───

export const caseResults: CaseResult[] = [
  {
    tag: { en: "Immigration", es: "Inmigración" },
    headline: { en: "Approval secured after prior filing delays", es: "Aprobación obtenida tras demoras previas en la solicitud" },
    summary: { en: "Client faced repeated delays and a prior denial. We restructured the filing strategy, prepared comprehensive supporting documentation, and secured approval on the refiled petition.", es: "El cliente enfrentó demoras repetidas y una negación previa. Reestructuramos la estrategia de presentación, preparamos documentación de soporte integral y obtuvimos la aprobación de la petición re-presentada." },
  },
  {
    tag: { en: "Personal Injury", es: "Lesiones Personales" },
    headline: { en: "Six-figure recovery after serious auto collision", es: "Recuperación de seis cifras tras grave colisión vehicular" },
    summary: { en: "After a multi-vehicle accident left our client with significant injuries and mounting medical bills, we negotiated a substantial settlement that covered all medical expenses, lost wages, and future care needs.", es: "Después de que un accidente múltiple dejó a nuestro cliente con lesiones significativas y facturas médicas crecientes, negociamos un acuerdo sustancial que cubrió todos los gastos médicos, salarios perdidos y necesidades de atención futura." },
  },
  {
    tag: { en: "Family Law", es: "Derecho Familiar" },
    headline: { en: "Favorable custody and parenting-time resolution", es: "Resolución favorable de custodia y tiempo de crianza" },
    summary: { en: "In a contested custody dispute, we presented a compelling case for our client's parenting plan, resulting in a favorable arrangement that prioritized the children's stability and well-being.", es: "En una disputa de custodia contenciosa, presentamos un caso convincente para el plan de crianza de nuestro cliente, resultando en un arreglo favorable que priorizó la estabilidad y bienestar de los hijos." },
  },
];

// ─── Attorney Spotlight ───

export const attorneySpotlight = {
  name: "Roberto Martínez",
  title: {
    en: "Founder & Managing Attorney",
    es: "Fundador y Abogado Principal",
  },
  statement: {
    en: "Every client who walks through our doors deserves the same level of commitment, preparation, and fight — regardless of the size of their case or the language they speak.",
    es: "Cada cliente que cruza nuestras puertas merece el mismo nivel de compromiso, preparación y lucha — sin importar el tamaño de su caso o el idioma que habla.",
  },
  credentials: [
    { en: "Licensed in Illinois", es: "Licenciado en Illinois" },
    { en: "Since 2019 — Serving Chicagoland & Beyond", es: "Desde 2019 — Sirviendo Chicagoland y Más Allá" },
    { en: "Bilingual (English/Spanish)", es: "Bilingüe (Inglés/Español)" },
    { en: "N.D. Illinois (select matters S.D. Illinois)", es: "N.D. Illinois (asuntos selectos S.D. Illinois)" },
  ],
  cta: {
    team: { en: "Meet the Team", es: "Conozca al Equipo" },
    book: { en: "Book Consultation", es: "Agendar Consulta" },
  },
};

// ─── How It Works ───

export const howItWorks = {
  sectionTitle: { en: "How It Works", es: "Cómo Funciona" },
  steps: [
    { number: "01", title: { en: "Consult", es: "Consulta" }, description: { en: "Tell us about your situation. We'll listen, assess your options, and outline a clear path forward.", es: "Cuéntenos sobre su situación. Escucharemos, evaluaremos sus opciones y delinearemos un camino claro hacia adelante." } },
    { number: "02", title: { en: "Strategy", es: "Estrategia" }, description: { en: "We build a tailored legal strategy based on your goals, timeline, and the specifics of your case.", es: "Construimos una estrategia legal personalizada basada en sus objetivos, cronograma y los detalles de su caso." } },
    { number: "03", title: { en: "Action", es: "Acción" }, description: { en: "We execute with precision — filing, negotiating, litigating — keeping you informed at every step.", es: "Ejecutamos con precisión — presentando, negociando, litigando — manteniéndolo informado en cada paso." } },
    { number: "04", title: { en: "Resolution", es: "Resolución" }, description: { en: "We drive your case to the strongest possible outcome, then help you understand what comes next.", es: "Llevamos su caso al mejor resultado posible, y luego le ayudamos a entender qué sigue." } },
  ],
};

// ─── Locations ───

export const offices: Office[] = [
  {
    city: { en: "Chicago (Little Village)", es: "Chicago (Little Village)" },
    address: "4115 W 26th St, Chicago, IL 60623",
    phone: "(773) 818-3020",
    mapUrl: "https://maps.google.com/?q=4115+W+26th+St+Chicago+IL+60623",
  },
  {
    city: { en: "Aurora", es: "Aurora" },
    address: "5 W Downer Place, Aurora, IL 60506",
    phone: "(773) 818-3020",
    mapUrl: "https://maps.google.com/?q=5+W+Downer+Place+Aurora+IL+60506",
  },
  {
    city: { en: "Kankakee County", es: "Condado de Kankakee" },
    address: "256 S Washington St, Kankakee, IL 60901",
    phone: "(773) 818-3020",
    mapUrl: "https://maps.google.com/?q=256+S+Washington+St+Kankakee+IL+60901",
  },
];

// ─── Resources Strip ───

export const resourceTeasers: ResourceTeaser[] = [
  { icon: "Calculator", title: { en: "Settlement Calculator", es: "Calculadora de Acuerdo" }, description: { en: "Estimate the potential value of your personal injury claim.", es: "Estime el valor potencial de su reclamo por lesiones personales." }, href: "/resources/settlement-calculator", esHref: "/es/recursos/calculadora-acuerdo" },
  { icon: "FileCheck", title: { en: "G-28 Filing Guide", es: "Guía de Formulario G-28" }, description: { en: "Step-by-step guide to the G-28 immigration form.", es: "Guía paso a paso del formulario de inmigración G-28." }, href: "/resources/g28-guide", esHref: "/es/recursos/guia-g28" },
  { icon: "HelpCircle", title: { en: "Top FAQs", es: "Preguntas Frecuentes" }, description: { en: "Answers to the most common legal questions we receive.", es: "Respuestas a las preguntas legales más comunes que recibimos." }, href: "/resources/faq", esHref: "/es/recursos/preguntas-frecuentes" },
  { icon: "Newspaper", title: { en: "Latest Insights", es: "Últimos Artículos" }, description: { en: "Legal updates, guides, and analysis from our team.", es: "Actualizaciones legales, guías y análisis de nuestro equipo." }, href: "/resources/insights", esHref: "/es/recursos/articulos" },
];

// ─── Final CTA ───

export const finalCTA = {
  headline: {
    en: "Don't wait until it's too late.",
    es: "No espere hasta que sea demasiado tarde.",
  },
  subline: {
    en: "Every legal matter has a deadline. Contact Martinez Law today for a confidential consultation.",
    es: "Cada asunto legal tiene un plazo. Contacte a Martinez Law hoy para una consulta confidencial.",
  },
  bilingual: {
    en: "Se habla español — bilingual support available on every call.",
    es: "Se habla español — atención bilingüe disponible en cada llamada.",
  },
  cta: {
    book: { en: "Book Consultation", es: "Agendar Consulta" },
    call: { en: "Call Now", es: "Llamar Ahora" },
  },
  portal: {
    en: "Existing client? Access your portal",
    es: "¿Cliente existente? Acceda a su portal",
  },
};

// ─── Footer ───

export const footer = {
  tagline: {
    en: "Practical strategy. Fast execution. Communication you can count on.",
    es: "Estrategia práctica. Ejecución rápida. Comunicación en la que puede confiar.",
  },
  copyright: {
    en: "© 2024 Martinez Law LLC. All rights reserved.",
    es: "© 2024 Martinez Law LLC. Todos los derechos reservados.",
  },
  disclaimer: {
    en: "The information on this website is for general informational purposes only. Nothing on this site should be taken as legal advice for any individual case or situation.",
    es: "La información en este sitio web es solo para fines informativos generales. Nada en este sitio debe tomarse como asesoría legal para ningún caso o situación individual.",
  },
  links: {
    privacy: { en: "Privacy Policy", es: "Política de Privacidad" },
    terms: { en: "Terms of Service", es: "Términos de Servicio" },
    accessibility: { en: "Accessibility", es: "Accesibilidad" },
  },
};

// ─── Labels ───

export const labels = {
  clientPortal: { en: "Client Portal", es: "Portal del Cliente" },
  callNow: { en: "Call Now", es: "Llamar" },
  bookConsultation: { en: "Book Consultation", es: "Agendar Consulta" },
  whatsapp: { en: "WhatsApp", es: "WhatsApp" },
  getDirections: { en: "Get Directions", es: "Obtener Direcciones" },
  viewAll: { en: "View All", es: "Ver Todo" },
  learnMore: { en: "Learn More", es: "Más Información" },
  meetTheTeam: { en: "Meet the Team", es: "Conozca al Equipo" },
};
